import { DashboardSLAPolicy, ResponseTimeMetric } from 'lib-dashboard-types';
import { PropsWithClassName } from 'lib-shared-types-utils';
import { memo } from 'react';

import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';

import { useQueueDashboardQueueContext } from '../../../queue/queueDashboardQueue.context';

import { QueueDashboardAverageResponseTimeMetric } from './components/average/averageResponseTimeMetric';
import { QueueDashboardSLAResponseTimeMetric } from './components/sla/slaResponseTimeMetric';

type QueueDashboardResponseTimeMetricProps = {
  /** The response time metric. */
  metric?: ResponseTimeMetric;
  /** The SLA policy which applies to this metric. */
  slaPolicy?: DashboardSLAPolicy;
} & PropsWithClassName;

export const QueueDashboardResponseTimeMetric = memo(
  function QueueDashboardResponseTimeMetric(
    props: QueueDashboardResponseTimeMetricProps,
  ): JSX.Element {
    const flags = useFeatureFlag(['sla-management']);
    const { queue } = useQueueDashboardQueueContext();

    // For backwards-compatibility, the old queue SLA thresholds are converted to the new SLA
    // policy format. Will be removed once the SLA management feature is fully rolled out.
    const legacySlaPolicy: DashboardSLAPolicy | undefined = queue
      ?.queue_thresholds?.sla_percentage
      ? {
          id: 0,
          metrics: [
            {
              type: 'duration',
              metric: 'response_time',
              percentage: queue.queue_thresholds.sla_percentage,
              target: (queue.queue_thresholds.sla_time_limit ?? 0) * 1000, // Legacy is in seconds
              achieved_percentage: props.metric?.sla_percentage ?? 0,
              evaluation_status: props.metric?.threshold_breached
                ? 'Breached'
                : 'Complied',
            },
          ],
        }
      : undefined;

    const slaPolicy = flags.slaManagement ? props.slaPolicy : legacySlaPolicy;

    return slaPolicy ? (
      <QueueDashboardSLAResponseTimeMetric {...props} slaPolicy={slaPolicy} />
    ) : (
      <QueueDashboardAverageResponseTimeMetric {...props} />
    );
  },
);
